'use client'

import {
  Combobox,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from '@headlessui/react'
import clsx from 'clsx'
import { IconNames } from 'lib/settings/icons'
import { AutoCompleteItem } from 'lib/types/AutoCompleteInput'
import { ControlledFormField, FormFieldCommonProps } from 'lib/types/Form'
import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type JSX,
} from 'react'
import { Controller, FieldValues, UseFormResetField } from 'react-hook-form'
import Icon from '../../Icon/Icon'
import { AddressInputAddress } from '../AddressInput/AddressInput'
import InputAddon, { InputAddonProps } from '../InputAddon/InputAddon'
import InputError from '../InputError/InputError'
import InputHint from '../InputHint/InputHint'
import InputLabel from '../InputLabel/InputLabel'

export interface AutoCompleteInputStandaloneProps
  extends InputAddonProps,
    Omit<FormFieldCommonProps, 'onChange' | 'defaultValue'> {
  /**
   * Optional class names to be passed to children components.
   */
  childrenClassNames?: Partial<Record<'ComboboxInput' | 'errorText', string>>
  /**
   * Optional default value
   */
  defaultValue?: string
  /**
   * Optional placeholder.
   */
  placeholder?: string
  /**
   * Optional HTML autocomplete attribute for the underlying input.
   */
  autoComplete?: string
  /**
   * Dropdown options from AutoComplete API
   */
  options?: AutoCompleteItem[]
  /**
   * AutoComplete API status
   */
  busy?: boolean
  /**
   * Optional blur event handler.
   */
  onBlur?: () => void
  /**
   * Optional focus event handler.
   */
  onFocus?: () => void
  /**
   * Optional change event handler.
   */
  onChange?: (item: AutoCompleteItem) => void
  /**
   * Optional input text handler
   */
  onInput?: (value: string) => void
  /**
   * Optional field setter.
   */
  setValue?: (value: string) => void
  /**
   * An optional filter function to check if an item matches the current query.
   */
  filter?: (query: string, item: AutoCompleteItem) => boolean
  /**
   * An optional callback that customizes the rendering of each option item.
   * Provides two parameters: `data` and `formatQueryMatch`
   * - `data` : The current option's data
   * - `formatQueryMatch`: A helper function that can be used on string to highlight/wrap parts that matches the current query
   * Expects a return value of an HTML string
   */
  optionFormat?: (
    data: AutoCompleteItem,
    formatQueryMatch: (text: string) => string,
  ) => string
  /**
   * Optional callback to control display string for the selected item
   */
  displayValue?: (item: any) => string
  /**
   * Custom Addon element
   */
  addon?: JSX.Element
  /**
   * Icon placed in the left side of the field
   */
  leadingIcon?: IconNames
  /**
   * Address object selected from the dropdown
   */
  address?: AddressInputAddress | null
  /**
   * Callback to reset the field when clearing input
   */
  onReset?: () => void
  /**
   * Maximum number of characters allowed in the input
   */
  maxLength?: number
  /**
   * When true, allows users to manually type values without selecting from the dropdown.
   * Disables the forced reselection error and auto-select on close behavior.
   */
  allowManualEntry?: boolean
  /**
   * When true, shows a search icon on the right side of the input field.
   */
  showSearchIcon?: boolean
  /**
   * Callback triggered when "Enter Address Manually" button is clicked in the dropdown.
   */
  onManualEntry?: () => void
}

export interface AutoCompleteInputProps
  extends Omit<ControlledFormField, 'defaultValue'>,
    AutoCompleteInputStandaloneProps {}

export const AutoCompleteInputStandalone = forwardRef<
  HTMLInputElement,
  AutoCompleteInputStandaloneProps
>(function AutoCompleteInputStandaloneWithRef(
  {
    addon,
    busy = false,
    childrenClassNames,
    className,
    defaultValue = '',
    disabled,
    displayValue,
    error,
    filter,
    hint,
    label,
    leadingIcon,
    name,
    onBlur,
    onChange,
    onFocus,
    onInput,
    maxLength,
    onReset,
    optionFormat,
    options = [],
    placeholder,
    setValue,
    allowManualEntry = false,
    showSearchIcon = false,
    onManualEntry,
    ...props
  }: AutoCompleteInputStandaloneProps,
  ref,
) {
  const internalRef = useRef<HTMLInputElement | null>(null)
  const setRefs = useCallback(
    (el: HTMLInputElement | null) => {
      internalRef.current = el
      if (typeof ref === 'function') ref(el)
      else if (ref)
        (ref as React.MutableRefObject<HTMLInputElement | null>).current = el
    },
    [ref],
  )

  const [isEnabled, setIsEnabled] = useState(
    'conditionalEdit' in props && props.conditionalEdit ? false : !disabled,
  )

  const defaultValueString = useMemo(
    () => (typeof defaultValue === 'string' ? defaultValue : ''),
    [defaultValue],
  )

  const [query, setQuery] = useState(defaultValueString)
  const selected = useRef<AutoCompleteItem | null>(null)

  const filterOptions = useCallback(
    (item: AutoCompleteItem) =>
      typeof filter === 'function' ? filter(query, item) : true,
    [query, filter],
  )

  const formatQueryMatch = useCallback(
    (text: string = '') =>
      text.replaceAll(
        new RegExp(query, 'gi'),
        (match) => `<span class="font-semibold">${match}</span>`,
      ),
    [query],
  )

  const changeHandler = useCallback(
    (selectedOption: AutoCompleteItem) => {
      const query =
        displayValue?.(selectedOption) ||
        selectedOption?.formatted_address ||
        selectedOption?.name ||
        ''

      selected.current = selectedOption

      onChange?.(selectedOption)
      setQuery(query)
    },
    [displayValue, onChange, selected, setQuery],
  )

  const updateInput = useCallback(
    (text: string) => {
      if (selected.current) selected.current = null
      setQuery(text)
      onInput?.(text)
      setValue?.(text)
    },
    [onInput, setValue],
  )

  const filteredOptions = useMemo(
    () => options.filter(filterOptions),
    [options, filterOptions],
  )

  const closeHandler = useCallback(() => {
    if (!selected.current && filteredOptions?.length && !allowManualEntry)
      changeHandler(filteredOptions[0])
    else if (allowManualEntry && !selected.current && query) setValue?.(query)
  }, [
    allowManualEntry,
    changeHandler,
    filteredOptions,
    query,
    selected,
    setValue,
  ])

  useEffect(() => {
    setQuery(defaultValueString)
    setValue?.(defaultValueString)
  }, [defaultValueString, setValue])

  const canClear = useMemo(() => {
    return !busy && query.length > 0
  }, [busy, query.length])

  return (
    <div className={clsx(`relative w-full`, error && 'text-error', className)}>
      <div className="flex items-center justify-between">
        {label && <InputLabel>{label}</InputLabel>}
        <InputAddon
          {...props}
          {...('conditionalEdit' in props && props.conditionalEdit
            ? { onEdit: setIsEnabled }
            : {})}
        />
        {addon ? addon : null}
      </div>
      <Combobox
        immediate
        onChange={changeHandler}
        defaultValue={defaultValueString}
        disabled={!isEnabled}
        onClose={closeHandler}
      >
        <div className="relative w-full">
          <ComboboxInput
            name={name}
            placeholder={placeholder}
            aria-label={label as string}
            autoComplete={props.autoComplete}
            displayValue={() => query}
            onBlur={onBlur}
            onFocus={onFocus}
            maxLength={maxLength}
            onChange={(event) => updateInput(event.target.value)}
            ref={setRefs}
            className={clsx(
              'w-full type-body-8 rounded-8 h-12 flex items-center px-4 my-2 transition-shadow lg:h-14',
              'disabled:bg-neutral-200 disabled:text-neutral-600 disabled:shadow-input-disabled disabled:cursor-not-allowed',
              'focus:shadow-input-focus focus:outline-none',
              'hover:shadow-input-active',
              error ? '!shadow-input-error' : 'shadow-input',
              leadingIcon && 'pl-12',
              (canClear || showSearchIcon) && 'pr-9 ',
              childrenClassNames?.ComboboxInput,
            )}
          />
          {leadingIcon ? (
            <Icon
              icon={leadingIcon}
              className="absolute w-6 h-full top-1/2 left-4 -translate-y-1/2 z-10"
            />
          ) : null}
          {canClear ? (
            <button
              className="absolute top-3 right-3 w-6 h-6 md:top-4 md:right-4"
              type="button"
              aria-label="clear input"
              onClick={() => {
                onReset?.()
                updateInput('')
              }}
            >
              <Icon icon="close" className="w-full h-full" />
            </button>
          ) : showSearchIcon ? (
            <Icon
              icon="search"
              className="absolute top-3 right-3 w-6 h-6 md:top-4 md:right-4 text-neutral-500"
            />
          ) : null}
          {busy && (
            <Icon
              icon="loading"
              className="animate-spin absolute top-4 right-4 w-6 h-6"
            />
          )}
        </div>
        <ComboboxOptions
          modal={false}
          key={name}
          className="empty:hidden relative mt-2 overflow-visible z-[100] w-[var(--input-width)]"
        >
          {!busy && (
            <div className="absolute left-0 right-0 overflow-auto z-10 max-h-[50vh] border border-neutral-400 rounded-8 shadow-dropdown bg-white">
              {/* Suggestions header with X close button */}
              {(showSearchIcon || onManualEntry) && (
                <div className="flex items-center justify-between px-4 py-2 border-b border-neutral-300">
                  <span className="type-body-8 text-neutral-500">
                    Suggestions
                  </span>
                  <button
                    type="button"
                    aria-label="close suggestions"
                    className="w-5 h-5 text-neutral-600"
                    onPointerDown={(e) => {
                      e.preventDefault()
                      internalRef.current?.blur()
                    }}
                  >
                    <Icon icon="close" className="w-full h-full" />
                  </button>
                </div>
              )}
              {/* create a custom option to allow typed-in values */}
              {query.length > 0 && !onManualEntry && !showSearchIcon && (
                <ComboboxOption
                  value={{ id: new Date().getTime(), name: query }}
                  className={clsx(
                    'cursor-pointer flex items-start justify-start gap-2 data-[focus]:bg-neutral-200 px-4 py-[10px] min-h-14 type-body-8 text-neutral-600',
                    !allowManualEntry && 'hidden',
                  )}
                >
                  {`Use "${query}"`}
                </ComboboxOption>
              )}
              {filteredOptions.map((option) => (
                <ComboboxOption
                  key={option.id}
                  value={option}
                  disabled={option.disabled ?? false}
                  className={clsx(
                    'flex items-start justify-start gap-2 data-[focus]:bg-neutral-200 px-4 py-[10px] min-h-14 type-body-8 text-neutral-600',
                    option.disabled
                      ? 'text-neutral-600 bg-neutral-200 cursor-not-allowed'
                      : 'cursor-pointer',
                  )}
                >
                  <Icon
                    iconUrl={option.icon_url}
                    className="w-6 h-6 shrink-0"
                  />
                  <div
                    dangerouslySetInnerHTML={{
                      __html: `
                      ${
                        typeof optionFormat === 'function'
                          ? optionFormat(option, formatQueryMatch)
                          : formatQueryMatch(
                              `${option.name} <br> ${option.address?.city}, ${option.address?.state} ${option.address?.zip}`,
                            )
                      }
                    `,
                    }}
                  ></div>
                </ComboboxOption>
              ))}
              {/* Enter Address Manually button */}
              {onManualEntry && (
                <button
                  type="button"
                  className="w-full bg-neutral-800 text-white py-3 text-center type-body-7 font-medium rounded-b-8 hover:bg-neutral-700 transition-colors"
                  onMouseDown={(e) => {
                    e.preventDefault()
                    internalRef.current?.blur()
                    onManualEntry()
                  }}
                >
                  Enter Address Manually
                </button>
              )}
            </div>
          )}
        </ComboboxOptions>
      </Combobox>
      <InputHint>{hint}</InputHint>
      <InputError>{error}</InputError>
    </div>
  )
})

export default function AutoCompleteInput({
  name,
  control,
  rules,
  onChange,
  displayValue,
  address,
  resetField,
  allowManualEntry = false,
  showSearchIcon = false,
  onManualEntry,
  ...props
}: AutoCompleteInputProps & { resetField?: UseFormResetField<FieldValues> }) {
  return (
    <Controller
      name={name || ''}
      control={control}
      rules={rules}
      render={({ field, fieldState }) => {
        const changeHandler = (data: AutoCompleteItem) => {
          field.onChange?.(displayValue?.(data))
          onChange?.(data)
        }

        const error =
          !allowManualEntry &&
          fieldState.isTouched &&
          !(field.value?.formatted_address || address)
            ? 'Please reselect your address from the drop down list'
            : fieldState.error?.message

        return (
          <AutoCompleteInputStandalone
            {...field}
            {...props}
            allowManualEntry={allowManualEntry}
            showSearchIcon={showSearchIcon && !address}
            onManualEntry={onManualEntry}
            displayValue={displayValue}
            error={error}
            onChange={changeHandler}
            setValue={field.onChange}
            onReset={() => resetField?.(name || '')}
          />
        )
      }}
    />
  )
}
